import { Box, TextField, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSettings } from "../contexts/SettingsContext";
import QuickActionsDropdown from "./QuickActionsDropdown";

const DateRangeSelector = () => {
  const { t } = useTranslation("common");
  const [selectedPeriod, setSelectedPeriod] = useState("Max");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const hasAppliedDefault = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { defaultDateRange, displayTemplate, displayTemplateLoading } =
    useSettings();

  const periods = [
    { label: "5D", value: "5D" },
    { label: "1M", value: "1M" },
    { label: "6M", value: "6M" },
    { label: "1Y", value: "1Y" },
    { label: "5Y", value: "5Y" },
    { label: "Max", value: "Max" },
    { label: "Custom", value: "Custom" },
  ];

  // Function to update URL params
  const updateURLParams = (start, end, period) => {
    const params = new URLSearchParams(location.search);

    if (period === "Max") {
      params.delete("period_start");
      params.delete("period_end");
    } else if (start && end) {
      params.set("period_start", start.toLocaleDateString("en-CA"));
      params.set("period_end", end.toLocaleDateString("en-CA"));
    }

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const calculateDateRange = (period) => {
    const today = new Date();
    let startDate,
      endDate = today;

    switch (period) {
      case "5D":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 5);
        break;
      case "1M":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        break;
      case "6M":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 6);
        break;
      case "1Y":
        startDate = new Date(today);
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      case "5Y":
        startDate = new Date(today);
        startDate.setFullYear(today.getFullYear() - 5);
        break;
      case "Max":
        return [null, null];
      default:
        return [null, null];
    }

    return [startDate, endDate];
  };

  const handlePeriodClick = (period) => {
    setSelectedPeriod(period);
    if (period !== "Custom") {
      const [start, end] = calculateDateRange(period);
      updateURLParams(start, end, period);
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      // Use date strings directly to avoid timezone conversion issues
      const params = new URLSearchParams(location.search);
      params.set("period_start", customStartDate);
      params.set("period_end", customEndDate);
      navigate(`${location.pathname}?${params.toString()}`);
    }
  };

  const handleQuickActionDateSelect = (startDate, endDate) => {
    // Switch to Custom mode to show the date inputs
    setSelectedPeriod("Custom");
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    // Update URL params immediately
    const params = new URLSearchParams(location.search);
    params.set("period_start", startDate);
    params.set("period_end", endDate);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const start = params.get("period_start");
    const end = params.get("period_end");

    if (start && end) {
      setCustomStartDate(start);
      setCustomEndDate(end);

      // Try to determine which period this corresponds to
      const startDate = new Date(start);

      // Check if it matches any predefined period exactly
      const [calculatedStart] = calculateDateRange("5D");
      const [calculatedStart1M] = calculateDateRange("1M");
      const [calculatedStart6M] = calculateDateRange("6M");
      const [calculatedStart1Y] = calculateDateRange("1Y");
      const [calculatedStart5Y] = calculateDateRange("5Y");

      if (calculatedStart && Math.abs(startDate - calculatedStart) < 86400000) {
        setSelectedPeriod("5D");
      } else if (
        calculatedStart1M &&
        Math.abs(startDate - calculatedStart1M) < 86400000
      ) {
        setSelectedPeriod("1M");
      } else if (
        calculatedStart6M &&
        Math.abs(startDate - calculatedStart6M) < 86400000
      ) {
        setSelectedPeriod("6M");
      } else if (
        calculatedStart1Y &&
        Math.abs(startDate - calculatedStart1Y) < 86400000
      ) {
        setSelectedPeriod("1Y");
      } else if (
        calculatedStart5Y &&
        Math.abs(startDate - calculatedStart5Y) < 86400000
      ) {
        setSelectedPeriod("5Y");
      } else {
        setSelectedPeriod("Custom");
      }
    } else if (
      displayTemplate?.date_range_config &&
      !hasAppliedDefault.current
    ) {
      // No URL params and a template exists — apply template default on first load
      hasAppliedDefault.current = true;
      setSelectedPeriod("Custom");
      setCustomStartDate(defaultDateRange.period_start);
      setCustomEndDate(defaultDateRange.period_end);
      const params = new URLSearchParams(location.search);
      params.set("period_start", defaultDateRange.period_start);
      params.set("period_end", defaultDateRange.period_end);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    } else if (!displayTemplateLoading) {
      // Only default to Max after template loading is complete
      // to avoid overriding a template date range that hasn't loaded yet
      setSelectedPeriod("Max");
      setCustomStartDate("");
      setCustomEndDate("");
    }
  }, [
    location.search,
    displayTemplate,
    displayTemplateLoading,
    defaultDateRange,
    navigate,
    location.pathname,
  ]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "4px",
          overflow: "hidden",
          backgroundColor: "transparent",
        }}
      >
        {periods.map((period, index) => (
          <Box
            key={period.value}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box
              component="button"
              onClick={() => handlePeriodClick(period.value)}
              sx={{
                border: "none",
                background: "none",
                padding: "8px 12px",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
                color: selectedPeriod === period.value ? "#1976d2" : "#5f6368",
                backgroundColor: "transparent",
                transition: "color 0.2s ease",
                "&:hover": {
                  color:
                    selectedPeriod === period.value ? "#1976d2" : "#202124",
                },
                "&:focus": {
                  outline: "none",
                },
              }}
            >
              {period.label}
            </Box>
            {index < periods.length - 1 && (
              <Box
                sx={{
                  width: "1px",
                  height: "20px",
                  backgroundColor: "divider",
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      {selectedPeriod === "Custom" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            type="date"
            label={t("dateRange.startDate")}
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
            onBlur={handleCustomDateChange}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
              },
              "& input[type='date']::-webkit-calendar-picker-indicator": {
                filter:
                  theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
                cursor: "pointer",
              },
            }}
          />
          <TextField
            type="date"
            label={t("dateRange.endDate")}
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            onBlur={handleCustomDateChange}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
              },
              "& input[type='date']::-webkit-calendar-picker-indicator": {
                filter:
                  theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
                cursor: "pointer",
              },
            }}
          />
        </Box>
      )}

      <QuickActionsDropdown
        onDateSelect={handleQuickActionDateSelect}
        currentStartDate={customStartDate}
        currentEndDate={customEndDate}
        isCustomMode={selectedPeriod === "Custom"}
      />
    </Box>
  );
};

export default DateRangeSelector;
